import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const SCOPES = [["full", "Full set"], ["front", "Front cover only"], ["rear", "Rear cover only"], ["clamps", "Clamps only"]];
const CLAMP_SIDES = [["front", "Front clamps"], ["rear", "Rear clamps"], ["both", "Front + rear"]];

// Factory One / Super Admin decisions. "Send new logo" is NOT here on purpose —
// mailing a logo is a Back Office self-resolve action (user rule Jun 2026).
export const ACTION_META = {
  warranty: { title: "🛠 Warranty replacement", cls: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  reorder: { title: "💳 Paid re-order", cls: "bg-indigo-600 hover:bg-indigo-700 text-white" },
  refund: { title: "💸 Refund", cls: "bg-rose-600 hover:bg-rose-700 text-white" },
  decline: { title: "✕ Declined / Resolved", cls: "bg-slate-700 hover:bg-slate-800 text-white" },
};

// Factory One / Super Admin decision — the note is the SOLUTION shown to Back Office + factory.
export function DecideDialog({ claim, action, onClose }) {
  const qc = useQueryClient();
  const { t } = useT();
  const [scope, setScope] = useState("full");
  const [clampsSide, setClampsSide] = useState("both");
  const [note, setNote] = useState("");
  const [returnRequested, setReturnRequested] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundKind, setRefundKind] = useState("");
  const meta = ACTION_META[action];
  const makesOrder = action === "warranty" || action === "reorder";
  const decide = useMutation({
    mutationFn: () => api.post(`/claims/${claim.id}/decide`, {
      action, scope, clamps_side: scope === "clamps" ? clampsSide : null, note: note.trim() || null, return_requested: returnRequested,
      refund_amount: action === "refund" && refundAmount !== "" ? Number(refundAmount) : null,
      refund_kind: action === "refund" ? refundKind : null,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["refunds"] });
      const sr = r.data.shopify_refund;
      if (sr?.error) toast.error(`Shopify refund failed — ${sr.error}. Factory One request was still filed.`, { duration: 12000 });
      else toast.success(r.data.summary || "Saved", { duration: 8000 });
      if (r.data.customer_refund_kind === "partial" && r.data.shopify_admin_url) window.open(r.data.shopify_admin_url, "_blank", "noopener");
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const country = (claim.order?.ship_country || "").toUpperCase();
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="claim-decide-dialog">
        <DialogHeader><DialogTitle>{t(meta.title)} · {claim.po_number}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          {makesOrder && (
            <div>
              <Label className="text-xs">{t("What should Factory One make?")}</Label>
              <div className="mt-1 grid grid-cols-2 gap-2" data-testid="claim-scope">
                {SCOPES.map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setScope(k)} data-testid={`claim-scope-${k}`}
                          className={`rounded-md border px-3 py-2 text-left text-xs font-semibold ${scope === k ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
                    {t(l)}
                  </button>
                ))}
              </div>
              {scope === "clamps" && (
                <div className="mt-2" data-testid="claim-clamps-side">
                  <Label className="text-xs">{t("Which clamps?")}</Label>
                  <div className="mt-1 grid grid-cols-3 gap-2">
                    {CLAMP_SIDES.map(([k, l]) => (
                      <button key={k} type="button" onClick={() => setClampsSide(k)} data-testid={`claim-clamps-side-${k}`}
                              className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${clampsSide === k ? "border-amber-600 bg-amber-50 text-amber-900" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
                        {t(l)}
                      </button>
                    ))}
                  </div>
                  <div className="text-[11px] text-amber-800 mt-1">{t("Clamps have no box of their own — at Packing they are confirmed, photographed and put inside another order's box.")}</div>
                </div>
              )}
              <div className="text-[11px] text-slate-500 mt-1">
                {t("A new order")} <span className="font-mono font-semibold">{claim.po_number}-{action === "warranty" ? "W" : "R"}</span> {t("starts in Production at Factory One with the same vehicle, codes, colour and customer.")}
              </div>
            </div>
          )}
          {action === "refund" && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{t("Customer refund in Shopify")} <span className="text-red-600">*</span></Label>
                <div className="mt-1 grid grid-cols-2 gap-2" data-testid="claim-refund-kind">
                  <button type="button" onClick={() => setRefundKind("full")} data-testid="claim-refund-kind-full"
                          className={`rounded-md border px-3 py-2 text-left text-xs ${refundKind === "full" ? "border-rose-600 bg-rose-50 text-rose-900" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
                    <div className="font-semibold">💳 {t("Full refund")}</div>
                    <div className="text-[11px] opacity-80">{t("Shopify refunds the whole order to the original payment method automatically")}</div>
                  </button>
                  <button type="button" onClick={() => setRefundKind("partial")} data-testid="claim-refund-kind-partial"
                          className={`rounded-md border px-3 py-2 text-left text-xs ${refundKind === "partial" ? "border-amber-600 bg-amber-50 text-amber-900" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
                    <div className="font-semibold">✂ {t("Partial refund")}</div>
                    <div className="text-[11px] opacity-80">{t("Opens the Shopify order so you refund the exact amount by hand")}</div>
                  </button>
                </div>
                {!claim.shopify_admin_url && <div className="text-[11px] text-rose-700 mt-1">{t("This order has no Shopify order ID — the Shopify refund cannot run automatically.")}</div>}
              </div>
              <div>
                <Label className="text-xs">{t("Factory One charge-back (RMB)")} <span className="text-slate-400">({t("blank = batch price")})</span></Label>
                <Input data-testid="claim-refund-amount" type="number" step="0.01" min="0" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} className="mt-1" placeholder="0.00" />
                <div className="text-[11px] text-slate-500 mt-1">{t("Factory One gets an approval request in Billing; once approved it is deducted from their first unpaid bill.")}</div>
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs">{action === "decline" ? t("Why declined / how resolved") : t("Solution")} <span className="text-slate-400">· {t("shown to Back Office & Factory One, auto-translated")}</span> {action === "decline" && <span className="text-red-600">*</span>}</Label>
            <Textarea data-testid="claim-decide-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                      placeholder={action === "decline" ? t("Why is the claim declined / resolved?") : t("What will fix it for the customer?")} className="mt-1" />
          </div>
          {action !== "decline" && (
            <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs" data-testid="claim-return-toggle">
              <input type="checkbox" checked={returnRequested} onChange={(e) => setReturnRequested(e.target.checked)} data-testid="claim-return-checkbox" className="mt-0.5" />
              <span>
                <b>{t("We need the old set back")}</b> — {t("a Return label step opens on this claim: buy the label in Shopify Admin")}
                {" "}({country === "US" ? "USPS → U.S. address" : "Canada Post → Concord, ON"}) {t("and the CRM e-mails it to the customer with our regular box size.")}
              </span>
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={onClose} disabled={decide.isPending} data-testid="claim-decide-cancel">{t("Cancel")}</Button>
          <Button data-testid="claim-decide-confirm" onClick={() => decide.mutate()} disabled={decide.isPending || (action === "decline" && !note.trim()) || (action === "refund" && !refundKind)} className={meta.cls}>
            {decide.isPending && action === "refund" ? t("Refunding…") : t(meta.title)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
